import { memo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import styles from "./Footer.module.css";
import { fetchSettings } from "./settingsSlice";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const settings = useSelector(function (state) {
    return state.settings.data;
  });

  useEffect(
    function () {
      var currentLanguage = i18n.language || "en";
      currentLanguage = currentLanguage.split("-")[0];

      dispatch(fetchSettings(currentLanguage));
    },
    [dispatch, i18n.language]
  );

  const handleFooterClick = function (event, sectionId) {
    event.preventDefault();

    const scrollToElement = function () {
      const element = document.getElementById(sectionId);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
        });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToElement, 150);
    } else {
      scrollToElement();
    }
  };

  const socialLinks = [
    {
      icon: "twitter-x",
      url: settings ? settings.xUrl : "",
    },
    {
      icon: "instagram",
      url: settings ? settings.instagram : "",
    },
    {
      icon: "youtube",
      url: settings ? settings.youtube : "",
    },
    {
      icon: "tiktok",
      url: settings ? settings.tiktok : "",
    },
    {
      icon: "whatsapp",
      url: settings && settings.whatsapp ? `https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}` : "",
    },
  ];

  const quickLinks = [
    {
      id: "home",
      label: "navbar.home",
    },
    {
      id: "services",
      label: "navbar.services",
    },
    {
      id: "about",
      label: "navbar.about",
    },
    {
      id: "categories",
      label: "navbar.categories",
    },
  ];

  const footerLogo =
    settings && settings.logo
      ? settings.logo
      : "/logo.png";

  const footerTitle =
    settings && settings.title
      ? settings.title
      : "Takween";

  const footerDescription =
    settings && settings.desc
      ? settings.desc
      : t("footer.description");

  return (
    <footer className={styles.footer} id="contact">
      <div className="container py-5">
        <div className={`${styles.footerContent} p-4 p-md-5`}>
          <div className="row g-4">
            <div className="col-xl-3 col-lg-6 col-12">
              <div className={styles.logoWrapper}>
                <img
                  src={footerLogo}
                  alt={footerTitle}
                  className="mb-3"
                />
              </div>

              <h5 className="text-white mb-3">
                {footerTitle}
              </h5>

              <p className="text-white-50">
                {footerDescription}
              </p>

              <div className="d-flex gap-3 mt-4">
                {socialLinks.map(function (social) {
                  if (!social.url) {
                    return null;
                  }

                  return (
                    <a
                      key={social.icon}
                      href={social.url}
                      className={styles.socialIcon}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.icon}
                    >
                      <i
                        className={`bi bi-${social.icon}`}
                      ></i>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-12">
              <h5 className="text-uppercase mb-4">
                {t("footer.quickLinks")}
              </h5>

              <ul className={styles.footerList}>
                {quickLinks.map(function (link) {
                  return (
                    <li key={link.id}>
                      <a
                        href={`#${link.id}`}
                        onClick={function (event) {
                          handleFooterClick(
                            event,
                            link.id
                          );
                        }}
                      >
                        {t(link.label)}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="col-xl-3 col-lg-6 col-12">
              <h5 className="text-uppercase mb-4">
                {t("footer.ourServices")}
              </h5>

              <ul className={styles.footerList}>
                <li>
                  <Link to="/privacy">
                    {t("footer.privacy")}
                  </Link>
                </li>

                <li>
                  <Link to="/terms">
                    {t("footer.terms")}
                  </Link>
                </li>

                <li>
                  <Link to="/faq">
                    {t("footer.faq")}
                  </Link>
                </li>
              </ul>
            </div>

            <div
              className={`col-xl-3 col-lg-6 col-12 ${styles.contactColumn}`}
            >
              {(settings?.address || settings?.phone || settings?.email) && (
                <h5 className="text-uppercase mb-4">
                  {t("footer.contactTitle")}
                </h5>
              )}

              {settings?.address && (
                <div className={`${styles.contactRow} mb-4`}>
                  <i
                    className={`bi bi-geo-alt ${styles.contactIcon}`}
                  ></i>

                  <div className={styles.contactText}>
                    <h6 className="mb-1 text-white">
                      {t("footer.locationLabel")}:
                    </h6>

                    <span className="text-white-50">
                      {settings.address}
                    </span>
                  </div>
                </div>
              )}

              {settings?.phone && (
                <div className={`${styles.contactRow} mb-4`}>
                  <i
                    className={`bi bi-telephone-fill ${styles.contactIcon}`}
                  ></i>

                  <div className={styles.contactText}>
                    <h6 className="mb-1 text-white">
                      {t("footer.callLabel")}:
                    </h6>

                    <span className="text-white-50">
                      {settings.phone}
                    </span>
                  </div>
                </div>
              )}

              {settings?.email && (
                <div className={styles.contactRow}>
                  <i
                    className={`bi bi-envelope-fill ${styles.contactIcon}`}
                  ></i>

                  <div className={styles.contactText}>
                    <h6 className="mb-1 text-white">
                      {t("footer.emailLabel")}:
                    </h6>

                    <a
                      href={`mailto:${settings.email}`}
                      className="text-white-50 text-decoration-none"
                    >
                      {settings.email}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);